/*
  TYOHAAR Mushak — Ganesh's mouse, coming for the modaks.

  A faithful port of the Claude Design study in the project root. The phases,
  their durations, the speeds, the easing, the gait maths and the cursor
  reaction are all that file's, kept to the number.

    peek  -> look -> walk -> settle -> grab -> flee -> off
    900ms         19          480      460            34 speed

  THE ONE THING THAT IS NOT A COPY, AND WHY
    The study moves him with `style.left = x + '%'`, where the percentage is of
    the strip. A transform is better — `left` re-lays-out the page on every
    frame, a transform does not — but `translateX(50%)` means half of the
    ELEMENT's own width, not half the strip. Ported literally it moves a mouse
    roughly fifty pixels instead of halfway across, which looks exactly like a
    mouse that will not walk.

    So the percentage is resolved against the measured strip width and applied
    in pixels. Same position, same motion, no layout per frame.

  Everything else is the study: the same `x` in strip-percent, the same phase
  names, the same constants.
*/

/*
  `facing`, not `dir`.

  Every HTMLElement already owns a `dir` property — the text-direction
  attribute. Assigning 1 to it on a custom element sets the ATTRIBUTE, which
  rejects the value and reads back as an empty string. `scaleX()` with nothing
  in it is invalid, and an invalid value makes the browser throw away the whole
  transform declaration — so the flip, the bob, the crouch and the squash all
  went with it. The mouse walked stiffly and never turned around.

  Same reason `stageWidth` is not `width`.
*/
/*
  Both overridden per section; these are only the fallbacks.

  HEIST_EVERY is now the time he spends fully OFF the strip before coming
  back, not a period he is somewhere inside. It used to be the study's
  `every * 0.75 + random * every * 0.6` — a spread of ten to nineteen seconds
  off one number, which read as him wandering off rather than doing rounds.
  A plain wait is what was asked for and is also easier to reason about: the
  setting says four, he is gone four.
*/
const HEIST_EVERY = 4000;
const FIRST_RAID = 1000;
// How long a stolen modak stays missing. Under one raid cycle, so the row is
// usually full again by the time he comes back.
const REFILL_AFTER = 8000;

/*
  Where his snout is, as a fraction of his own width from his centre.

  The drawing is 130 units wide and the tip of the nose lands at 119, so the
  snout is 91.5% across the box. The element is centred on his position, which
  puts the nose 0.415 of a body-width ahead of it.

  This is the number that decides where he stops. It was a flat 5.5% of the
  strip — which, with him at 5% of the strip on a desktop, left him a whole
  body-length short and looking like he was taking the modak by telekinesis.
  On a phone he is 13% of the strip and the same 5.5% happened to be almost
  exactly right, which is why it only ever looked wrong on a desktop.
*/
const SNOUT = 119 / 130 - 0.5;

class TyohaarMushak extends HTMLElement {
  connectedCallback() {
    this.rat = this.querySelector('[data-part="rat"]');
    /*
      The plate's modaks are targets too, exactly as in the study.

      They are <g> inside one SVG rather than positioned boxes, so their
      position cannot be read off style.left — each carries its own data-x
      instead, and everything downstream treats the two kinds alike.
    */
    this.modaks = Array.from(this.querySelectorAll('.mk__modak, .mk__plate-modak'));
    if (!this.rat || !this.modaks.length) return;

    this.parts = {};
    this.rat.querySelectorAll('[data-part]').forEach((n) => {
      this.parts[n.dataset.part] = n;
    });

    this.every = (Number(this.dataset.interval) || 0) * 1000 || HEIST_EVERY;
    this.firstAt = (Number(this.dataset.firstDelay) || 0) * 1000 || FIRST_RAID;
    this.walkSpeed = Number(this.dataset.speed) || 19;
    this.summonOnHover = this.dataset.summon !== 'false';
    /*
      Whether the plate and the bowl may be robbed.

      Off: he takes only from the loose row across the middle, and the two
      vessels keep what is on them. They still answer a hover with a nudge —
      a modak that ignores the pointer reads as broken rather than as
      off-limits — he simply never goes for one.
    */
    this.raidVessels = this.dataset.raidVessels === 'true';
    /*
      The carried modak's size, with a value of its own on a phone.

      The ratio it is built from is the same at both widths, so in theory one
      number would do. It does not: on a small strip the whole scene is read
      at a glance and a modak that is merely large on a desktop is the first
      thing the eye lands on. The narrow plan takes a smaller share.
    */
    this.carryScale = (Number(this.dataset.carryScale) || 100) / 100;
    this.carryScaleMobile = (Number(this.dataset.carryScaleMobile) || 100) / 100;
    this.refill = (Number(this.dataset.refill) || 0) * 1000 || REFILL_AFTER;

    /*
      A seed only. measure() overwrites every one of these from the rendered
      position on the next line and again on the next frame — this is just
      something sane to hold if a rect comes back empty on a cold load.
    */
    this.targets = this.modaks.map((el, i) => ({
      key: i,
      el,
      x: parseFloat(el.style.getPropertyValue('--x')) || 50,
      plate: el.classList.contains('mk__plate-modak'),
    }));

    // The study's dust pool — ten spans, reused in turn.
    const sparkHost = this.querySelector('[data-part="sparks"]');
    this.sparks = sparkHost ? Array.from(sparkHost.children).map((n) => ({ n, life: 0 })) : [];
    this.sparkI = 0;
    this.lastSpark = 0;

    this.taken = {};
    this.x = -8;
    this.facing = 1;
    this.phase = 'off';
    this.gait = 0;
    this.headA = 0;
    this.headTarget = 0;
    this.carrying = false;
    this.seg = null;
    this.freezeUntil = 0;
    this.reacted = false;
    this.pointer = null;
    this.queued = null;
    this.running = false;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) return;

    this.bind();
    this.measure();
    // Again on the next frame: the plate is an inline SVG, and on a cold load
    // the first measure can land before it has been given its width.
    requestAnimationFrame(() => this.measure());
    this.t = performance.now();
    this.nextAt = this.t + this.firstAt;
    this.visible = true;
    this.start();
    this.watch();
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /* ---------------------------------------------------------------- setup */

  measure() {
    const stage = this.getBoundingClientRect();
    this.stageWidth = stage.width || 1;

    /*
      His own width as a share of the strip, measured rather than assumed.

      It is set in CSS as a percentage and differs between the two
      arrangements — 5 on a desktop, 13 on a phone — so the distance he needs
      to stop short cannot be a constant. Read here, where the strip is
      already being measured, and used by dirOffset below.
    */
    const rat = this.rat ? this.rat.getBoundingClientRect().width : 0;
    this.ratPct = stage.width ? (rat / stage.width) * 100 : 5;

    /*
      Where every target actually is, measured rather than declared.

      This started as a fix for the plate — three <g> inside one pixel-sized
      SVG, whose written percentages only described the desktop width. But the
      loose modaks have the same problem the moment they are allowed to stand
      somewhere different on a phone: an attribute can hold one number, and
      there are two positions.

      Reading the rendered position removes the question. The CSS decides where
      a modak stands at this width, and he walks to wherever that turned out to
      be — so a mobile layout can move things freely and nothing here needs to
      be told about it.

      A handful of rects on load and on resize, never during a run.
    */
    if (!this.targets || !stage.width) return;
    for (const t of this.targets) {
      const r = t.el.getBoundingClientRect();
      /*
        A zero-size box means the arrangement has taken this one out — the
        narrow plan drops three of the six modaks and the far diya. Marked
        rather than skipped: left alone it would keep its last known position
        and he would cross the strip to grab at something nobody can see.
      */
      t.gone = !r.width;
      if (t.gone) continue;
      t.x = ((r.left + r.width / 2 - stage.left) / stage.width) * 100;
      // Its width too, so the one he carries off can be made to match it.
      t.w = (r.width / stage.width) * 100;
    }
  }

  bind() {
    this.modaks.forEach((el, i) => {
      el.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'touch') this.request(i);
      });
      el.addEventListener('click', () => this.request(i));
    });

    // The study watches the pointer across the whole stage, which is what
    // lets him notice you standing over him mid-walk.
    this.addEventListener('pointermove', (e) => {
      const r = this.getBoundingClientRect();
      this.pointer = { x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 };
    });
    this.addEventListener('pointerleave', () => {
      this.pointer = null;
      this.headTarget = 0;
    });

    this.onResize = () => this.measure();
    window.addEventListener('resize', this.onResize);

    this.onVisibility = () => {
      if (document.hidden) this.stop();
      else if (this.visible) this.start();
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  watch() {
    this.observer = new IntersectionObserver(
      (entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible && !document.hidden) this.start();
        else this.stop();
      },
      { rootMargin: '160px' }
    );
    this.observer.observe(this);
  }

  start() {
    if (this.running) return;
    this.running = true;
    const now = performance.now();
    // Time is not allowed to have passed while nobody was looking, or he
    // arrives already halfway through a run he never made.
    const gone = now - (this.t || now);
    this.t = now;
    if (this.seg) this.seg.t0 += gone;
    if (this.nextAt) this.nextAt += gone;
    this.lastTick = now;
    this.raf = requestAnimationFrame(this.tick);

    /*
      The study's watchdog, and it earns its place.

      requestAnimationFrame is throttled — sometimes to nothing — in a
      background tab, a cross-origin frame, and the theme editor's preview.
      The rat then stands still with no error anywhere, which reads exactly
      like a broken trigger rather than a paused one.

      setInterval survives that. It only steps when rAF has actually gone
      quiet for more than two frames, so in the normal case it costs a
      comparison every 60ms and nothing else.
    */
    this.watchdog = setInterval(() => {
      if (!this.running) return;
      const n = performance.now();
      if (n - (this.lastTick || 0) > 120) this.step(n);
    }, 60);
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    clearInterval(this.watchdog);
  }

  /* -------------------------------------------------------------- helpers */

  ease(t, kind) {
    if (kind === 'in') return t * t;
    if (kind === 'out') return 1 - (1 - t) * (1 - t);
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  go(phase, to, speed, kind) {
    const dist = Math.abs(to - this.x);
    this.seg = {
      from: this.x,
      to,
      t0: performance.now(),
      dur: Math.max(220, (dist / speed) * 1000),
      kind: kind || 'inout',
    };
    this.phase = phase;
    if (to !== this.x) this.facing = to > this.x ? 1 : -1;
  }

  wait(phase, ms) {
    this.seg = { from: this.x, to: this.x, t0: performance.now(), dur: ms, kind: 'inout' };
    this.phase = phase;
  }

  /*
    How far short of the modak he stops, so his NOSE lands on it.

    Derived from his own width rather than fixed, because that width is a
    share of the strip and changes with the arrangement. Stopping his centre
    on the modak would bury it in his shoulder; stopping a body-length short
    is what made him look like he was taking it from across the room.
  */
  dirOffset(t) {
    const reach = (this.ratPct || 5) * SNOUT;
    return t.x - this.x > 0 ? reach : -reach;
  }

  /*
    The modak in his mouth — sized to the one he took, and put at his mouth.

    Two things were wrong here, and the second was hiding behind the first.

    It used to be drawn at a flat scale of 0.5, about an eighth of his length
    against modaks nearer two fifths of it, so one vanished and a smaller one
    appeared. Sizing it from the real one fixed that and immediately exposed
    the anchor: the group was pinned at (109, 63.5), which is his BELLY. At
    the old size that read as tucked under his chin; at the new size it was
    plainly a modak held against his stomach with his face above it.

    Anchored at (109, 47) now — the snout, between the head circle and the
    jaw. The path is 34 wide and 40 tall inside a 130x92 body, so centring it
    means offsetting by half of each, scaled.

    And deliberately NOT at full size. Matching the real modak is arithmetically
    right and looks wrong: two fifths of his own length is something a mouse
    drags, not something it carries. Two thirds of that reads as the same
    modak and as a mouse that can lift it.
  */
  fitCarry(t) {
    const c = this.parts.carry;
    if (!c || !t || !t.w || !this.ratPct) return;
    // Which plan is in force, judged the same way the stylesheet judges it.
    const narrow = this.stageWidth < 700;
    const k = narrow ? this.carryScaleMobile : this.carryScale;
    const s = (t.w / this.ratPct) * (130 / 34) * k;
    c.setAttribute('transform', `translate(${109 - 20 * s} ${47 - 23 * s}) scale(${s})`);
  }

  setOpacity(v) {
    this.rat.style.opacity = v;
  }

  /* The ones he is allowed to want: on the strip, not taken, not on a vessel. */
  available() {
    return this.targets.filter(
      (t) => !this.taken[t.key] && !t.gone && (this.raidVessels || !t.plate)
    );
  }

  pickTarget() {
    const free = this.available();
    return free.length ? free[Math.floor(Math.random() * free.length)] : null;
  }

  /*
    Taken, and back again a good while later.

    The study put this at six to nine seconds, when a raid came round every
    ten to nineteen. Now that he returns four seconds after leaving, a full
    cycle is nearer ten — so the first modak was reappearing while he was
    still carrying off the second, and the row never looked any emptier for
    all his work.

    The spread is kept, and deliberately: without it several modaks stolen in
    a row would all come back together, which reads as a reset rather than as
    a gap being filled.
  */
  steal(key) {
    this.taken[key] = true;
    const el = this.targets[key]?.el;
    if (el) el.classList.add('is-taken');
    setTimeout(() => {
      this.taken[key] = false;
      const e = this.targets[key]?.el;
      if (e) e.classList.remove('is-taken');
    }, this.refill + Math.random() * this.refill * 0.4);
  }

  /* --------------------------------------------------------------- sparks */

  /*
    The dust off his back feet as he bolts — the study's numbers exactly.

    Thrown BEHIND him: the velocity is negated against `facing`, so the dust
    goes the way he came from rather than travelling with him. That is the
    whole reason it reads as speed and not as a trail of decorations.

    Spawned from a pool and overwritten in turn. One every 70ms of flee is
    around a dozen a run; creating and dropping that many nodes each time is
    what makes a hand-rolled particle system stutter.
  */
  emitSpark(now) {
    const s = this.sparks[this.sparkI % this.sparks.length];
    this.sparkI++;
    if (!s) return;

    s.life = 1;
    s.t0 = now;
    s.dur = 850 + Math.random() * 500;
    s.x = this.x - this.facing * 2 + (Math.random() - 0.5) * 1.2;
    s.y = 47.5 + (Math.random() - 0.5) * 0.8;
    s.vx = -this.facing * (0.6 + Math.random() * 0.9);
    s.vy = -(0.5 + Math.random() * 0.9);
  }

  drawSparks(now) {
    for (const s of this.sparks) {
      if (!s.life) continue;
      const t = (now - s.t0) / s.dur;
      if (t >= 1) {
        s.life = 0;
        s.n.style.opacity = 0;
        continue;
      }
      s.n.style.opacity = (1 - t) * 0.8;
      s.n.style.left = s.x + s.vx * t * 2.4 + '%';
      // The squared term is gravity — they arc rather than drifting off.
      s.n.style.top = s.y + s.vy * t * 2.2 - t * t * 0.6 + '%';
      s.n.style.transform = `rotate(${t * 180}deg)`;
    }
  }

  /* ------------------------------------------------------------- requests */

  request(key) {
    if (this.reduced || this.taken[key]) return;

    const t = this.targets[key];
    if (!t || t.gone) return;

    // The modak answers on the frame it is hovered, whatever he is doing.
    t.el.classList.remove('is-nudged');
    void t.el.offsetWidth;
    t.el.classList.add('is-nudged');

    if (!this.summonOnHover) return;

    /*
      The nudge above happens either way; the summon does not.

      A modak on the plate still rocks when you point at it — silence there
      would read as a broken control rather than a protected one — but he is
      not sent for it.
    */
    if (t.plate && !this.raidVessels) return;

    if (this.phase === 'grab' || this.phase === 'flee' || this.carrying) {
      this.queued = key;
      return;
    }

    this.target = t;
    this.reacted = false;

    if (this.phase === 'off') {
      this.x = t.x < 50 ? -8 : 108;
      this.facing = t.x < 50 ? 1 : -1;
      this.setOpacity(1);
      this.go('peek', t.x < 50 ? 3 : 97, 16, 'out');
    } else {
      this.go('walk', t.x - this.dirOffset(t), 21, 'inout');
    }
  }

  advance(now) {
    const t = this.target;

    switch (this.phase) {
      case 'peek':
        this.wait('look', 900);
        this.headTarget = this.pointer ? this.headTarget : -6;
        break;

      case 'look':
        this.headTarget = 0;
        this.go('walk', t.x - this.dirOffset(t), this.walkSpeed, 'inout');
        break;

      case 'walk':
        this.wait('settle', 480);
        this.headTarget = 10;
        break;

      case 'settle':
        this.wait('grab', 460);
        break;

      case 'grab':
        this.carrying = true;
        this.headTarget = 0;
        this.stretch = 1.16;
        this.fitCarry(t);
        this.steal(t.key);
        this.go('flee', t.x < 50 ? -10 : 110, 34, 'in');
        break;

      case 'flee': {
        this.phase = 'off';
        this.seg = null;
        this.carrying = false;
        this.setOpacity(0);
        /*
          Off the strip, then back after exactly `every`.

          The one exception is a modak hovered while he was escaping: that is
          a direct request, and making someone wait the full gap for a thing
          they just asked for reads as the hover having failed.
        */
        this.nextAt = now + (this.queued != null ? 900 : this.every);
        break;
      }

      default:
        this.seg = null;
    }
  }

  /* ----------------------------------------------------------------- loop */

  tick = (now) => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.tick);
    this.step(now);
  };

  step(now) {
    // Stamped here rather than in tick(), so the watchdog's own steps count
    // as ticks too and the two cannot both drive the same frame.
    this.lastTick = now;
    const dt = Math.min(64, now - this.t);
    this.t = now;
    const frozen = now < this.freezeUntil;

    // He notices a cursor loitering over him, stops, looks at it, then bolts.
    if (this.pointer && (this.phase === 'walk' || this.phase === 'flee') && !this.reacted && !frozen) {
      const near = Math.abs(this.pointer.x - this.x) < 7 && Math.abs(this.pointer.y - 70) < 22;
      if (near) {
        this.reacted = true;
        this.freezeUntil = now + 780;
        this.frozenAt = now;
        this.headTarget = Math.max(-16, Math.min(16, (this.pointer.x - this.x) * this.facing * 2.4));
      }
    }
    if (frozen && this.pointer) {
      this.headTarget = Math.max(-16, Math.min(16, (this.pointer.x - this.x) * this.facing * 2.4));
    }

    if (this.seg) {
      if (frozen) {
        // The run is paused, not abandoned: pushing the start forward keeps
        // the remaining distance intact.
        this.seg.t0 += dt;
      } else if (this.frozenAt && now - this.frozenAt < 1400 && (this.phase === 'walk' || this.phase === 'flee')) {
        this.seg.t0 -= dt * 0.45; // bolt away after the stare
      }

      const t = Math.min(1, (now - this.seg.t0) / this.seg.dur);
      const prev = this.x;
      this.x = this.seg.from + (this.seg.to - this.seg.from) * this.ease(t, this.seg.kind);
      const moved = Math.abs(this.x - prev);
      this.gait += moved * 1.15 + (this.phase === 'flee' ? moved * 0.5 : 0);
      if (t >= 1) this.advance(now);
    } else if (this.phase === 'off' && !frozen && now > this.nextAt) {
      const t = this.queued != null ? this.targets[this.queued] : this.pickTarget();
      this.queued = null;
      if (t && !this.taken[t.key] && !t.gone && (this.raidVessels || !t.plate)) {
        this.target = t;
        this.reacted = false;
        this.x = t.x < 50 ? -8 : 108;
        this.facing = t.x < 50 ? 1 : -1;
        this.setOpacity(1);
        this.go('peek', t.x < 50 ? 3 : 97, 15, 'out');
      } else {
        // Nothing free to take — every modak is out. Ask again after the
        // same gap rather than a number of its own.
        this.nextAt = now + this.every;
      }
    }

    // One every 70ms of flee — the study's cadence.
    if (this.phase === 'flee' && !frozen && now - this.lastSpark > 70) {
      this.lastSpark = now;
      this.emitSpark(now);
    }

    this.draw(now, dt, frozen);
    this.drawSparks(now);
  }

  draw(now, dt, frozen) {
    const running = (this.phase === 'walk' || this.phase === 'flee' || this.phase === 'peek') && !frozen;
    const step = running ? Math.sin(this.gait * 1.7) : 0;
    const bob = running ? Math.abs(Math.sin(this.gait * 1.7)) * 1.6 : Math.sin(now / 900) * 0.5;
    const crouch = this.phase === 'settle' || this.phase === 'grab' ? 2.2 : 0;

    // The study's `left: x%` of the strip, resolved here against the measured
    // width so it can be a transform instead.
    this.rat.style.transform = `translate3d(${(this.x / 100) * this.stageWidth}px, 0, 0)`;

    this.stretch = this.stretch ? this.stretch + (1 - this.stretch) * (1 - Math.pow(0.92, dt / 16.67)) : 1;

    if (this.parts.svg) {
      this.parts.svg.style.transform =
        `scaleX(${this.facing}) translateY(${crouch - bob}px) scale(${this.stretch}, ${2 - this.stretch})`;
    }

    const amp = this.phase === 'flee' ? 26 : 17;
    this.rotate('legB', step * amp);
    this.rotate('legF', -step * amp);
    this.rotate('legB2', -step * amp * 0.8);
    this.rotate('legF2', step * amp * 0.8);
    this.rotate('tail', Math.sin(this.gait * 1.7 + 1.1) * (running ? 11 : 4) + (this.phase === 'flee' ? -8 : 0));

    this.headA += (this.headTarget - this.headA) * 0.12;
    this.rotate('head', this.headA * (frozen ? 1 : 0.6));

    if (this.parts.shadow) {
      const lift = bob / 1.6;
      this.parts.shadow.style.transform = `scale(${1 - lift * 0.22}, ${1 - lift * 0.3})`;
      this.parts.shadow.style.opacity = 0.09 * (1 - lift * 0.35);
    }

    if (this.parts.carry) this.parts.carry.style.opacity = this.carrying ? 1 : 0;
  }

  rotate(part, deg) {
    const node = this.parts[part];
    if (node) node.style.transform = `rotate(${deg}deg)`;
  }
}

customElements.define('tyoh-mushak', TyohaarMushak);
